import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { CheckCircle2, Info, Star, Clock, MapPin, ArrowUpRight } from "lucide-react";
import { CATEGORY_META, TIME_META } from "@/lib/experiences/categoryMeta";
import { isUserFacingValidUrl } from "@/lib/experiences/linkIntegrity";
import { normalizeExperienceTitleForDisplay } from "@/lib/experiences/titleDisplay";
import { track } from "@/lib/analytics";
import type { MarketplaceDetailSurface } from "@/lib/analytics";
import { logMarketplaceEvent } from "@/lib/marketplaceEventLog";
import { getTrustedExperienceImage, getTrustedExperienceImageFallback, getTrustedExperienceImageAlt } from "./mediaRelevance";
import { SelectionAction } from "./SelectionAction";
import { getExcursionLabel } from "./marketplaceInventory";
import type { MarketplaceItem } from "./marketplaceInventory";
import { getProviderLinkState, getProviderSupportingText, getProviderContinueCta, formatProviderDisplayName, getPriceAvailabilityTrustNote } from "./providerLinkState";
import { buildProviderClickedProps } from "./detailAnalytics";
import { FOCUS_RING } from "./interactionStyles";
import { usePreferences } from "@/lib/hooks/usePreferences";
import { useFxRates } from "@/lib/hooks/useFxRates";
import { convertPriceForDisplay, formatConvertedPrice } from "@/lib/experiences/currencyConversion";
import { useI18n } from "@/lib/i18n/useI18n";
import { INTL_LOCALE_BY_LANGUAGE } from "@/lib/i18n/languages";

// ─── Featured shelf ─────────────────────────────────────────────────────────────
// A small, editorial discovery moment before the full inventory — one primary
// card plus up to two secondary ones. Reused for both the "Recomendado por
// Sift Trip" shelf and the dynamic themed teaser ("Gastronomía", "Buenas para la
// mañana"...). Items shown here are never preselected and still appear again
// in the main list below with the exact same selection state — adding or
// removing here updates the same `selectedIds` the full list reads from, so
// there is nothing to keep "in sync": there is only one source of truth.
export function FeaturedShelf({
  title,
  icon,
  items,
  cityHint,
  cityIata,
  rawQuery,
  showListHint = false,
  excursionIds,
  onAdd,
  onRemove,
  onOpenDetail,
}: {
  title:         string;
  icon?:         ReactNode;
  items:         MarketplaceItem[];
  cityHint?:     string | null;
  cityIata?:     string | null;
  /** Marketplace Media Relevance & Visual Trust V1 — see
   *  ExperienceMarketplaceRow's own doc for this prop. */
  rawQuery?:     string | null;
  /** Small caption clarifying these items also live in the full list below. */
  showListHint?: boolean;
  /** Marketplace Inventory Sections V1 — compositeIds of items the caller
   *  has classified as excursions (see groupMarketplaceByIntent). The shelf
   *  can mix city experiences and excursions when there aren't enough of
   *  the former (task 3), so each card needs its own membership check
   *  rather than one flag for the whole shelf. */
  excursionIds?: Set<string>;
  onAdd:         (compositeId: string, title: string) => void;
  onRemove:      (compositeId: string, title: string) => void;
  /** Marketplace Product Detail & Conversion V1 — opens ExperienceDetailDrawer
   *  for a card. Optional only so existing snapshot/tests that don't wire it
   *  don't crash; every real caller passes it. */
  onOpenDetail?: (item: MarketplaceItem, surface: MarketplaceDetailSurface) => void;
}) {
  if (items.length === 0) return null;
  const [primary, ...secondary] = items;

  return (
    <section className="mb-6">
      <div className="flex items-baseline gap-2 mb-2.5 flex-wrap">
        <div className="flex items-center gap-1.5">
          {icon}
          <h2
            className="text-[11px] font-bold uppercase tracking-[0.10em]"
            style={{ color: "oklch(0.120 0.040 258 / 0.65)" }}
          >
            {title}
          </h2>
        </div>
        {showListHint && (
          <span className="text-[10px] italic" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
            Destacadas de la lista completa
          </span>
        )}
      </div>
      <div className="flex flex-col gap-3">
        <FeaturedCard
          item={primary}
          cityHint={cityHint}
          cityIata={cityIata}
          rawQuery={rawQuery}
          large
          isExcursion={excursionIds?.has(primary.compositeId) ?? false}
          onAdd={() => onAdd(primary.compositeId, primary.exp.title)}
          onRemove={() => onRemove(primary.compositeId, primary.exp.title)}
          onOpenDetail={onOpenDetail && (() => onOpenDetail(primary, "featured_shelf"))}
        />
        {secondary.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {secondary.map(item => (
              <FeaturedCard
                key={item.compositeId}
                item={item}
                cityHint={cityHint}
                cityIata={cityIata}
                rawQuery={rawQuery}
                isExcursion={excursionIds?.has(item.compositeId) ?? false}
                onAdd={() => onAdd(item.compositeId, item.exp.title)}
                onRemove={() => onRemove(item.compositeId, item.exp.title)}
                onOpenDetail={onOpenDetail && (() => onOpenDetail(item, "featured_shelf"))}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FeaturedCard({
  item,
  cityHint,
  cityIata,
  rawQuery,
  large = false,
  isExcursion = false,
  onAdd,
  onRemove,
  onOpenDetail,
}: {
  item:          MarketplaceItem;
  cityHint?:     string | null;
  cityIata?:     string | null;
  rawQuery?:     string | null;
  large?:        boolean;
  isExcursion?:  boolean;
  onAdd:         () => void;
  onRemove:      () => void;
  onOpenDetail?: () => void;
}) {
  const { exp, neighborhood, isSelected } = item;
  const { t, language } = useI18n();
  const isProvider = exp.source_type === "provider";
  const catMeta  = CATEGORY_META[exp.category] ?? { label: exp.category, color: "oklch(0.50 0.06 200)" };
  const timeMeta = TIME_META[exp.time_of_day] ?? TIME_META.any;
  const hasSpecificTime = Boolean(exp.time_of_day) && exp.time_of_day !== "any";
  const hasDuration = typeof exp.duration_hours === "number" && exp.duration_hours > 0;
  const hasRating = typeof exp.rating === "number" && exp.rating > 0;
  const hasReviewCount = typeof exp.review_count === "number" && exp.review_count > 0;
  const hasProviderName = isProvider && typeof exp.provider_name === "string" && exp.provider_name.trim() !== "";
  const providerDisplayName = hasProviderName ? formatProviderDisplayName(exp.provider_name) : null;
  const isPriceOrientative = exp.price_from != null && exp.catalog_only === true;
  const isAvailabilityUncertain = isProvider
    && (exp.availability_status === "skipped_no_date" || exp.availability_status === "error");
  const uncertaintyNote = getPriceAvailabilityTrustNote(isPriceOrientative, isAvailabilityUncertain, language);
  const isTipBased = exp.pricing_model === "tip_based";

  // Reference Currency Display V1.1 — see ExperienceMarketplaceCard.tsx's
  // own comment for the full rationale (shared rates, no refetch on
  // currency change).
  const { currency: selectedCurrency } = usePreferences();
  const { data: fxRates } = useFxRates();
  const converted = convertPriceForDisplay({
    originalAmount: exp.price_from ?? null,
    originalCurrency: exp.currency ?? null,
    selectedCurrency,
    rates: fxRates?.rates,
    isTipBased,
  });

  const priceDisplay = isTipBased
    ? t("marketplace.price.tipBased")
    : exp.price_from != null
      ? (exp.price_from === 0
          ? t("marketplace.price.free")
          : t("marketplace.price.from", { price: formatConvertedPrice(converted.amount ?? exp.price_from, converted.currency) }))
      : null;
  const priceDisclaimer = converted.isReferenceConversion
    ? `${t("marketplace.price.disclaimer")}${
        fxRates?.as_of ? ` ${t("marketplace.price.disclaimerUpdated", { date: new Date(fxRates.as_of).toLocaleDateString(INTL_LOCALE_BY_LANGUAGE[language]) })}` : ""
      }`
    : undefined;
  const displayTitle = normalizeExperienceTitleForDisplay(exp.title, { isProvider, cityHint });

  // Card Quality V2: same booking-link semantics as the full-list row — a
  // real, valid booking_url earns the "Ver disponibilidad" CTA. Provider
  // Detail Discoverability Hotfix V1: now has the same explicit secondary
  // detail button as the full-list row, in addition to the image/title.
  const linkState = getProviderLinkState(exp);
  const showBookingCta = linkState === "bookable";
  // Provider Deep Links & Viator Images V1: same honesty rule as the
  // full-list row — a provider item without a valid booking_url says so,
  // it never renders an anchor with no real destination.
  const showMissingProviderLink = linkState === "missing_link";
  const providerSupportingText = getProviderSupportingText(exp, language);

  // Truthful badge copy: "top"/"good" read generically, but when the item's
  // own time_of_day is genuinely morning, the more specific real claim is
  // used instead of the generic "Buen match" — never inferred, only ever
  // reflecting a field the backend actually set. Marketplace Visual QA +
  // Mobile Polish V1: matches ExperienceMarketplaceRow's "Recomendada por
  // Sift Trip" wording exactly — the same item can appear in both a themed
  // teaser shelf (title unrelated to Sift Trip, e.g. "Gastronomía") and the main
  // list below, and the badge must say the same thing about it either way.
  const badgeLabel = item.recommendationBadge === "top"
    ? "Recomendada por Sift Trip"
    : item.recommendationBadge === "good"
      ? (hasSpecificTime && exp.time_of_day === "morning" ? "Buena para la mañana" : "Buen match")
      : null;

  const metaLine1 = [
    hasDuration ? `${exp.duration_hours}h` : null,
    hasSpecificTime ? timeMeta.label : null,
    catMeta.label,
    // Metadata-level, not a badge — same quiet fact row as duration/time/
    // category. The provider trust label above stays unchanged.
    isExcursion ? getExcursionLabel(exp) : null,
  ].filter((s): s is string => Boolean(s)).join(" · ");
  const locationLabel = neighborhood || null;

  const heroSrc = getTrustedExperienceImage(exp, rawQuery, cityHint);
  const [imgSrc, setImgSrc] = useState<string | null>(heroSrc);
  useEffect(() => {
    setImgSrc(heroSrc);
  }, [heroSrc]);
  const isRealPhoto = imgSrc === exp.image_url && isUserFacingValidUrl(exp.image_url);
  const handleImgError = () => {
    const fallback = getTrustedExperienceImageFallback(exp, cityHint);
    if (imgSrc === exp.image_url && fallback) setImgSrc(fallback);
    else setImgSrc(null);
  };

  return (
    <div
      className="flex flex-col rounded-lg overflow-hidden"
      style={{
        background: "oklch(0.998 0.003 72)",
        border:     "1px solid oklch(0.880 0.024 72 / 0.35)",
      }}
    >
      {/* Launch Conversion UX V1, task 1: a taller frame than before so a
          real provider photo reads as a genuine hero image, not a thumbnail
          — applies uniformly (not provider-vs-local, unlike the full-list
          row) since these two cards sit side-by-side in a grid and mismatched
          heights there would look broken, not "secondary".
          Marketplace Product Detail & Conversion V1, task 4 / Provider
          Detail Discoverability Hotfix V1: the image is an ADDITIONAL open-
          detail trigger, not the only one — the explicit "Ver detalle" text
          button near the bottom of the card is the one guaranteed-visible
          affordance. A plain <button>, not a link, and it never wraps the
          rest of the card (the booking CTA and SelectionAction below stay
          independently clickable). */}
      <button
        type="button"
        onClick={onOpenDetail}
        disabled={!onOpenDetail}
        aria-label={`Ver detalle de "${displayTitle}"`}
        className={`relative w-full flex items-center justify-center text-left overflow-hidden transition-transform duration-150 active:scale-[0.99] motion-reduce:transition-none motion-reduce:active:scale-100 ${FOCUS_RING}`}
        style={{ height: large ? "172px" : "118px", background: "oklch(0.960 0.010 72)", border: "none", padding: 0, cursor: onOpenDetail ? "pointer" : "default" }}
      >
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={getTrustedExperienceImageAlt(exp, rawQuery, displayTitle)}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={handleImgError}
          />
        ) : (
          <span className="text-[10px] font-semibold" style={{ color: catMeta.color, opacity: 0.5 }}>
            {catMeta.label}
          </span>
        )}
        {badgeLabel && (
          <span
            className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.04em] px-2 py-0.5 rounded-full"
            style={{ background: "oklch(0.998 0.003 72 / 0.92)", color: "#E8194B" }}
          >
            <Star size={9} aria-hidden style={{ fill: "currentColor" }} />
            {badgeLabel}
          </span>
        )}
      </button>

      <div className="flex flex-col px-3 py-2.5">
        <span
          className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.06em]"
          style={{ color: isProvider ? "oklch(0.46 0.12 145)" : "oklch(0.545 0.020 60 / 0.55)" }}
          title={providerDisplayName ?? undefined}
        >
          {isProvider ? <CheckCircle2 size={9} aria-hidden /> : <Info size={9} aria-hidden />}
          {isProvider ? "Verificado con proveedor" : "Referencia local"}
          {providerDisplayName && (
            <span className="normal-case font-semibold" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
              · {providerDisplayName}
            </span>
          )}
        </span>
        <h3
          className={`font-display font-bold leading-snug mt-0.5 ${large ? "text-[15px]" : "text-[13.5px]"}`}
          style={{ color: "var(--color-heading-navy)" }}
        >
          {onOpenDetail ? (
            <button
              type="button"
              onClick={onOpenDetail}
              className={`text-left hover:opacity-75 transition-opacity rounded-sm ${FOCUS_RING}`}
              style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "inherit", cursor: "pointer" }}
            >
              {displayTitle}
            </button>
          ) : displayTitle}
        </h3>

        {/* Exact-match badge — Hotfix: Global Currency Formatting + Exact
            Query Match Boost + No-Transfers Enforcement V1, Parte B. Own
            line under the title — never the same top-left slot the
            recommendation badge occupies over the image, and never the
            price/rating row at the bottom. */}
        {exp.semantic_exact_query_match && (
          <p
            className="inline-flex items-center gap-1 text-[10px] font-semibold mt-0.5"
            style={{ color: "oklch(0.545 0.020 60 / 0.7)" }}
          >
            <Star size={9} aria-hidden style={{ fill: "#E8194B", color: "#E8194B" }} />
            {t("marketplace.badge.exactMatch")}
          </p>
        )}

        {/* Meta row 1 — duration · recommended time · category, same grouping as the row */}
        {metaLine1 && (
          <p className="text-[10px] mt-1 leading-snug flex items-center gap-1" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
            {hasDuration && <Clock size={10} aria-hidden style={{ flexShrink: 0 }} />}
            {metaLine1}
          </p>
        )}

        {/* Meta row 2 — location and price/availability status, merged */}
        {(locationLabel || uncertaintyNote) && (
          <p
            className="text-[10px] mt-0.5 leading-snug flex items-center flex-wrap gap-x-1 gap-y-0.5"
            style={{ color: "oklch(0.545 0.020 60 / 0.42)" }}
          >
            {locationLabel && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={10} aria-hidden style={{ flexShrink: 0 }} />
                {locationLabel}
              </span>
            )}
            {locationLabel && uncertaintyNote && <span aria-hidden="true">·</span>}
            {uncertaintyNote && <span>{uncertaintyNote}</span>}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 mt-1.5">
          <span className="inline-flex items-center gap-2">
            {priceDisplay && (
              <span className="text-[12px] font-bold" style={{ color: "oklch(0.120 0.040 258)" }} title={priceDisclaimer}>{priceDisplay}</span>
            )}
            {hasRating && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold" style={{ color: "#E8194B" }}>
                <Star size={9} aria-hidden style={{ fill: "currentColor" }} />
                {exp.rating!.toFixed(1)}
                {hasReviewCount && ` (${exp.review_count})`}
              </span>
            )}
          </span>
          <SelectionAction isSelected={isSelected} onAdd={onAdd} onRemove={onRemove} itemTitle={displayTitle} />
        </div>

        {/* Provider Detail Discoverability Hotfix V1 — "Ver detalle" is now
            an explicit, visible secondary action here, not just the image/
            title (task 2/4). It's a sibling of the booking CTA, never
            nested inside it, so it can never compete with or be mistaken
            for "Añadir a comparar" (a separate row above) or "Ver
            experiencia" (still the visually primary, filled action). */}
        <div className="flex items-center gap-2.5 flex-wrap mt-1.5">
          {showBookingCta ? (
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              <a
                href={exp.booking_url!}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => {
                  track("booking_clicked", {
                    provider_name: exp.provider_name ?? undefined,
                    city:          exp.neighborhood ?? "",
                    price_from:    exp.price_from ?? undefined,
                  });
                  track("provider_product_clicked", {
                    experience_id:                exp.id,
                    iata:                         cityIata ?? undefined,
                    source_type:                  exp.source_type ?? null,
                    provider_name:                exp.provider_name ?? null,
                    marketplace_relevance_class:  exp.marketplace_relevance_class ?? null,
                    surface:                      "featured_shelf",
                  });
                  // Launch Analytics & Funnel Instrumentation V1 — canonical
                  // funnel event, fired alongside the two legacy ones above.
                  const _clickProps = buildProviderClickedProps(exp, cityIata, "featured_shelf");
                  track("marketplace_provider_clicked", _clickProps);
                  // Admin Metrics UX V1 — same event, alongside PostHog.
                  logMarketplaceEvent("marketplace_provider_clicked", {
                    destination_iata: _clickProps.destination_iata,
                    provider_name:    _clickProps.provider_name,
                    category:         _clickProps.category,
                    surface:          _clickProps.surface,
                    provider_clicked: true,
                    has_booking_url:  _clickProps.has_booking_url,
                    link_variant:     _clickProps.link_variant,
                  });
                }}
                className={`inline-flex items-center gap-1 text-[10px] font-bold self-start px-2.5 py-1 rounded-md transition-all duration-150 hover:opacity-85 active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 ${FOCUS_RING}`}
                style={{ color: "oklch(0.998 0.003 72)", background: "oklch(0.120 0.040 258)" }}
              >
                {getProviderContinueCta(exp, language)}
                <ArrowUpRight size={9} aria-hidden />
              </a>
              <span className="text-[9px]" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                {providerSupportingText}
              </span>
            </span>
          ) : showMissingProviderLink ? (
            <span
              aria-disabled="true"
              className="inline-flex items-center text-[10px] font-bold px-2 py-1 rounded-md"
              style={{ color: "oklch(0.545 0.020 60 / 0.55)", background: "oklch(0.545 0.020 60 / 0.08)", cursor: "not-allowed" }}
            >
              {t("compare.linkUnavailable")}
            </span>
          ) : null}
          {onOpenDetail && (
            <button
              type="button"
              onClick={onOpenDetail}
              className={`inline-flex items-center text-[10px] font-semibold transition-opacity hover:opacity-75 rounded-sm ${FOCUS_RING}`}
              style={{ color: "oklch(0.545 0.020 60 / 0.65)", background: "none", border: "none", padding: 0, cursor: "pointer" }}
            >
              Ver detalle
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
